import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function Privacy() {
  const navigate = useNavigate();
  
  const primaryColor = '#00C6AE';
  const textPrimary = '#111827';
  const textSecondary = '#6B7280';
  const borderColor = '#E5E7EB';

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: '#FFFFFF',
      color: textPrimary,
      minHeight: '100vh',
      padding: '80px 20px 40px',
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'transparent',
            border: 'none',
            color: primaryColor,
            cursor: 'pointer',
            fontSize: '16px',
            marginBottom: '32px',
            textDecoration: 'underline',
          }}
        >
          ← Back
        </button>

        <h1 style={{
          fontSize: '42px',
          fontWeight: 700,
          marginBottom: '16px',
          color: textPrimary,
        }}>
          Privacy Policy
        </h1>

        <p style={{
          color: textSecondary,
          fontSize: '14px',
          marginBottom: '40px',
        }}>
          Last updated: January 27, 2026
        </p>

        <div style={{
          lineHeight: 1.8,
          color: textPrimary,
        }}>
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              1. Introduction
            </h2>
            <p style={{ marginBottom: '16px' }}>
              SignalCue ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our recruitment intelligence platform.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              2. Information We Collect
            </h2>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px', marginTop: '16px' }}>
              2.1 Account Information
            </h3>
            <p style={{ marginBottom: '16px' }}>
              When you create an account, we collect:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Organisation name, website, and contact details</li>
              <li>Administrator name, email address, and job title</li>
              <li>Country, timezone, and data residency preferences</li>
              <li>Agency size and recruitment sectors</li>
            </ul>

            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px', marginTop: '16px' }}>
              2.2 Data You Provide
            </h3>
            <p style={{ marginBottom: '16px' }}>
              We process recruitment data that you provide or connect through integrations, including:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Candidate information (names, contact details, employment history)</li>
              <li>Company information</li>
              <li>Placement records and activity history</li>
              <li>ATS integration data</li>
            </ul>

            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px', marginTop: '16px' }}>
              2.3 Usage Data
            </h3>
            <p style={{ marginBottom: '16px' }}>
              We automatically collect information about how you use the Service, including access logs, feature usage, and system performance data.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              3. How We Use Your Information
            </h2>
            <p style={{ marginBottom: '16px' }}>
              We use the information we collect to:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Provide, maintain, and improve the Service</li>
              <li>Process and analyze your recruitment data to generate insights</li>
              <li>Send you service-related communications and updates</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
            </ul>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              4. Data Storage and Security
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Your data is stored in secure data centers located in the region you specify during sign-up (e.g., Singapore, United Kingdom, Thailand). We implement industry-standard security measures including:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Encryption in transit and at rest</li>
              <li>Multi-tenant data isolation</li>
              <li>Regular security audits and assessments</li>
              <li>Access controls and authentication</li>
              <li>Backup and disaster recovery procedures</li>
            </ul>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              5. Data Sharing and Disclosure
            </h2>
            <p style={{ marginBottom: '16px' }}>
              We do not sell your data. We may share your information only in the following circumstances:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li><strong>Service Providers:</strong> With trusted third-party service providers who assist in operating our Service (e.g., cloud hosting, email delivery), subject to strict confidentiality obligations</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with notice to affected users</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
            </ul>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              6. Your Rights and Choices
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Depending on your location, you may have the following rights:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li><strong>Access:</strong> Request access to your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your data (subject to legal retention requirements)</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
              <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              To exercise these rights, please contact us at privacy@signalcue.ai.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              7. Data Retention
            </h2>
            <p style={{ marginBottom: '16px' }}>
              We retain your data for as long as necessary to provide the Service and fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When you terminate your account, we will delete or anonymize your data in accordance with our data retention policies, subject to legal requirements.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              8. International Data Transfers
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Your data is stored in the data residency region you select during sign-up. We do not transfer your data outside your selected region except as necessary to provide the Service or as required by law, and always in compliance with applicable data protection laws.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              9. Children's Privacy
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              10. Changes to This Privacy Policy
            </h2>
            <p style={{ marginBottom: '16px' }}>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: primaryColor }}>
              11. Contact Us
            </h2>
            <p style={{ marginBottom: '16px' }}>
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p style={{ marginBottom: '16px' }}>
              Email: privacy@signalcue.ai<br />
              Address: SignalCue, [Your Address]
            </p>
          </section>
        </div>

        <div style={{
          marginTop: '60px',
          paddingTop: '32px',
          borderTop: `1px solid ${borderColor}`,
          textAlign: 'center',
          color: textSecondary,
          fontSize: '14px',
        }}>
          <p>&copy; 2026 SignalCue. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
